import { useTranslation } from 'react-i18next';
import { useCustomerFormatters } from '../../shared/hooks/useCustomerFormatters.js';
import type { LoyaltyPreviewPricingResult } from './hooks/useCheckoutPricingPreview.js';

export function CheckoutLoyaltyPreview(input: {
  readonly loyalty: LoyaltyPreviewPricingResult | null | undefined;
  readonly isLoading: boolean;
  /** Session charge currency — required on charge path (G12; never CZK invent via formatters). */
  readonly currency?: string;
}): JSX.Element | null {
  const { t } = useTranslation('customer');
  const { formatPrice: formatPriceRaw } = useCustomerFormatters();
  const formatPrice = (amountMajor: number): string =>
    formatPriceRaw(amountMajor, input.currency);

  if (input.isLoading) {
    return (
      <p className="text-sm text-[var(--color-on-surface-muted)]" data-testid="checkout-loyalty-preview-loading">
        {t('loyalty.checkoutPreviewLoading')}
      </p>
    );
  }

  if (input.loyalty == null) {
    return null;
  }

  const hasDiscount = input.loyalty.cartDiscountAmount > 0;
  const hasEarn = input.loyalty.estimatedEarnPoints > 0;

  if (!hasDiscount && !hasEarn) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3"
      data-testid="checkout-loyalty-preview"
    >
      {hasDiscount ? (
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-[var(--color-on-surface-muted)]">{t('loyalty.checkoutDiscountLabel')}</span>
          <span className="font-medium text-[var(--color-on-surface)]">
            −{formatPrice(input.loyalty.cartDiscountAmount)}
          </span>
        </div>
      ) : null}
      {hasEarn ? (
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-[var(--color-on-surface-muted)]">{t('loyalty.checkoutEarnLabel')}</span>
          <span className="font-medium text-[var(--color-on-surface)]" data-testid="checkout-loyalty-earn-points">
            +{input.loyalty.estimatedEarnPoints}
          </span>
        </div>
      ) : null}
    </div>
  );
}
